/**
 * Сайдбар блоку nmhg/embedded: зміст за заголовками + перемикач спрощеної мови.
 *
 * Чому не main.js. Там уже є будівник змісту (`document.querySelectorAll('.art-nav')`),
 * але він розрахований на ОДНУ колонку контенту: бере перший
 * `.art-nav__content`, сканує h2 і будує список один раз, на завантаженні.
 * Тут колонок дві — звичайна й спрощена, — і зміст мусить перебудовуватись
 * при перемиканні. Тому корінь секції не несе класу 'art-nav' (main.js його
 * не бачить), а цей модуль веде і побудову, і підсвітку активного пункту.
 * Розмітка списку та назви класів — ті самі, що будує main.js, тож CSS
 * верстки працює без жодного власного правила.
 *
 * Логіка відступів при скролі (шапка + `top` липкого сайдбара + висота
 * списку на мобільному) продубльована з main.js навмисно: це той самий
 * контракт із версткою, і розходження тут одразу помітне оком.
 */

import { BREAKPOINTS } from '../config.js';

const { mqTouch } = BREAKPOINTS;

// Ключ той самий за принципом, що й у теми оформлення ('nmhg-theme').
const STORAGE_KEY = 'nmhg-simple-lang';

const sections = document.querySelectorAll('[data-embedded]');

sections.forEach(init);

function init(section) {
  const aside = section.querySelector('.art-nav__aside');
  const list = section.querySelector('.art-nav__list');
  const zones = [...section.querySelectorAll('[data-lang]')];

  // Список змісту НЕ обов'язковий: його вимикають окремою галочкою (у блоці —
  // «Показувати зміст», на синглі — налаштування сайдбару), а перемикач мови
  // від нього не залежить. Тому далі кожне звертання до list захищене.
  if (!aside || !zones.length) {
    return;
  }

  const header = document.querySelector('.header');
  const uid = Math.random().toString(36).slice(2, 7);

  let links = [];
  let headings = [];
  let active = null;

  const zoneFor = (mode) => zones.find((zone) => zone.dataset.lang === mode);
  const visibleZone = () => zones.find((zone) => !zone.hidden) ?? zones[0];

  function metrics() {
    return {
      headerHeight: header?.offsetHeight || 0,
      navListHeight: list?.offsetHeight || 0,
      stickyTop: parseFloat(getComputedStyle(aside).top) || 0,
    };
  }

  const topOf = (el) => Math.round(el.getBoundingClientRect().top + window.scrollY);

  /** Лінія, вище якої заголовок вважається пройденим. */
  function threshold() {
    const { headerHeight, navListHeight, stickyTop } = metrics();
    const base = window.scrollY + headerHeight + stickyTop + 16;

    // На мобільному список лежить над контентом і сам займає місце.
    return window.innerWidth <= mqTouch ? base + navListHeight : base;
  }

  /** Підтягнути активний пункт до середини списку, щоб він не тікав із поля зору. */
  function center(link) {
    const box = link.getBoundingClientRect();
    const area = list.getBoundingClientRect();

    if (window.innerWidth <= mqTouch) {
      list.scrollBy({ left: box.left - area.left - area.width / 2 + box.width / 2, behavior: 'smooth' });
    } else {
      list.scrollBy({ top: box.top - area.top - area.height / 2 + box.height / 2, behavior: 'smooth' });
    }
  }

  function spy() {
    if (!links.length) {
      return;
    }

    const line = threshold();
    let current = links[0];

    headings.forEach((heading, index) => {
      if (topOf(heading) <= line) {
        current = links[index];
      }
    });

    if (current !== active) {
      active?.classList.remove('is-active');
      current.classList.add('is-active');
      active = current;
      center(current);
    }
  }

  /** Зміст із заголовків видимої зони. h3 — фолбек, як у main.js. */
  function buildNav() {
    if (!list) {
      return;
    }

    const zone = visibleZone();

    // Режим у префіксі id обов'язковий. Без нього обидві зони отримували б
    // однакові id ('embedded-xxx-1', 'embedded-xxx-2', …): нумерація в кожній
    // починається з одиниці. Прихована зона з DOM нікуди не дівається, тож
    // document.getElementById віддавав ПЕРШИЙ збіг — заголовок звичайної
    // версії, — і клік у спрощеній мові скролив у нікуди.
    const mode = zone.dataset.lang || 'main';

    let nodes = [...zone.querySelectorAll('h2')].filter((node) => node.textContent.trim());

    if (!nodes.length) {
      nodes = [...zone.querySelectorAll('h3')].filter((node) => node.textContent.trim());
    }

    list.innerHTML = '';
    links = [];
    headings = nodes;
    active = null;

    if (!nodes.length) {
      return;
    }

    const nav = document.createElement('ul');
    nav.className = 'art-nav__nav';

    nodes.forEach((node, index) => {
      // Свій id ставимо, лише якщо його немає: чужий (з якоря блоку) —
      // це посилання, на яке вже можуть вести ззовні.
      if (!node.id) {
        node.id = `embedded-${uid}-${mode}-${index + 1}`;
      }

      const item = document.createElement('li');
      const link = document.createElement('a');

      link.href = `#${node.id}`;
      link.textContent = node.textContent.trim();

      item.appendChild(link);
      nav.appendChild(item);
      links.push(link);
    });

    list.appendChild(nav);
    links[0].classList.add('is-active');
    active = links[0];
  }

  /**
   * Показати зону.
   *
   * reveal — для секцій із анімацією появи: спостерігач
   * (`[data-threshold-desktop]` у main.js) створюється на завантаженні, і
   * зона, схована в той момент, могла лишитись напівпрозорою назавжди.
   * Тому при перемиканні просто ставимо клас так, ніби вона вже з'явилась.
   */
  function apply(mode, reveal) {
    zones.forEach((zone) => {
      zone.hidden = zone.dataset.lang !== mode;
    });

    const toggle = section.querySelector('.simple-lang__toggle');

    if (toggle) {
      toggle.classList.toggle('is-active', 'simple' === mode);
      toggle.setAttribute('aria-pressed', String('simple' === mode));
    }

    if (reveal) {
      visibleZone()
        .querySelectorAll('[data-threshold-desktop]')
        .forEach((el) => el.classList.add('is-threshold'));
    }

    buildNav();
    spy();
  }

  const toggle = section.querySelector('.simple-lang__toggle');

  if (toggle && zoneFor('simple')) {
    // main.js вішає на першу .simple-lang__toggle власний слухач, який лише
    // тоглить 'is-active'. Разом із нашим станом це давало б подвійне
    // перемикання, тож підміняємо вузол копією — слухачі не клонуються.
    const fresh = toggle.cloneNode(true);
    toggle.replaceWith(fresh);

    fresh.addEventListener('click', () => {
      const next = visibleZone().dataset.lang === 'simple' ? 'main' : 'simple';

      apply(next, true);

      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch (error) {
        // Приватний режим — вибір просто не переживе перезавантаження.
      }
    });
  }

  let saved = null;

  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    saved = null;
  }

  // Сервер віддає відкритою звичайну зону, тож анімацію «як уже показано»
  // домальовуємо лише тоді, коли одразу відкриваємо іншу.
  const start = 'simple' === saved && zoneFor('simple') ? 'simple' : 'main';

  apply(start, 'simple' === start);

  window.addEventListener('scroll', spy, { passive: true });

  list?.addEventListener('click', (event) => {
    const link = event.target.closest('a');

    if (!link) {
      return;
    }

    event.preventDefault();

    const id = link.getAttribute('href').slice(1);

    // Шукаємо спершу У ВИДИМІЙ ЗОНІ, і лише потім по всьому документу.
    // Свої id ми робимо унікальними (див. buildNav), але якір може задати й
    // редактор — той самий у звичайній і спрощеній версії того самого блоку.
    // Тоді getElementById повернув би прихований заголовок сусідньої зони.
    const target =
      visibleZone().querySelector(`#${CSS.escape(id)}`) || document.getElementById(id);

    if (!target) {
      return;
    }

    const { headerHeight, navListHeight, stickyTop } = metrics();
    let top = topOf(target) - headerHeight - stickyTop;

    if (window.innerWidth <= mqTouch) {
      top -= navListHeight;
    }

    window.scrollTo({ top, behavior: 'smooth' });
  });
}
